// 절차 현무암·용암: 추가 텍스처 없이 면 종류마다 한 번 그린다. 보행면은 항상 0m.
import * as THREE from 'three/webgpu';
import { attribute, float, fract, length, max, mix, mx_noise_float, mx_worley_noise_vec2, luminance, positionGeometry, positionWorld, screenUV, sin, smoothstep, time, uv, vec2, vec3, vec4 } from 'three/tsl';
import { Cell, cellAt, type MapDef, type MapGrid } from '../../shared/levels';
import { merge } from '../geo';
import { BASALT_LIP, CHASM_DEPTH, chasmAt, emberEdges } from './emberLayout';
import { mulberry32 } from './rng';
import { tiled } from './surfaces';

/** 바닥은 기존 판석의 큰 무늬를 재사용하고 일부 금에만 약한 잿불을 남긴다. */
export function emberFloorMaterial(map: MapDef, tex: THREE.Texture | null): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.97 });
  const wp = positionWorld.xz;
  const stone = tex ? luminance(tiled(tex, wp, 5.8, 3).col) : float(0.3);
  const cells = mx_worley_noise_vec2(wp.mul(0.42));
  const seam = float(1).sub(smoothstep(0.012, 0.045, cells.y.sub(cells.x)));
  const ash = mx_noise_float(vec3(wp.mul(0.19), 7)).mul(0.5).add(0.5);
  m.colorNode = mix(vec3(0.09, 0.105, 0.13), vec3(0.28, 0.27, 0.26), stone.mul(1.5).clamp(0, 1))
    .mul(mix(float(0.75), float(1.12), ash)).mul(float(1).sub(seam.mul(0.48)));
  // 반사광은 판정 바깥 바닥에만 부드럽게 번진다. 테두리 선이나 크림색은 쓰지 않는다.
  let heat: THREE.Node<'float'> = float(0);
  for (const r of map.lava ?? []) {
    const q = wp.sub(vec2((r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2)).abs().sub(vec2((r.x1 - r.x0) / 2, (r.z1 - r.z0) / 2));
    heat = heat.max(float(1).sub(smoothstep(0, 1.8, length(q.max(vec2(0))))));
  }
  const warmCrack = seam.mul(smoothstep(0.57, 0.72, ash));
  m.emissiveNode = vec3(0.22, 0.035, 0.003).mul(warmCrack).add(vec3(0.3, 0.055, 0.006).mul(heat));
  return m;
}
export function basaltMaterial(chasm = false): THREE.MeshStandardNodeMaterial {
  const m = new THREE.MeshStandardNodeMaterial({ roughness: 0.96 });
  const n = mx_noise_float(positionWorld.mul(vec3(1.8, 0.22, 1.8))).mul(0.5).add(0.5);
  const strata = sin(positionWorld.y.mul(3.8).add(n.mul(2))).mul(0.5).add(0.5);
  m.colorNode = mix(vec3(0.08, 0.095, 0.12), vec3(0.23, 0.21, 0.19), strata).mul(n.mul(0.3).add(0.8));
  const low = float(1).sub(smoothstep(chasm ? -8 : 0, chasm ? 0.5 : 2, positionWorld.y));
  m.emissiveNode = vec3(0.018, 0.03, 0.05).add(vec3(chasm ? 1.1 : 0.15, chasm ? 0.19 : 0.025, 0.005).mul(low).mul(strata.mul(0.35).add(0.65)));
  return m;
}
/** hot = 스테이지 지도(S6) 웅덩이: 껍질 판이 작고 틈이 넓어 멀리서도 "끓는 용암"으로 읽힌다(검푸른 판으로 보이지 않게) */
export function magmaMaterial(hot = false): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial();
  // F2-F1 경계는 각진 냉각판의 연결된 균열이다. 판 자체가 초당 약 2.5cm로 천천히 흐른다.
  const wp = positionWorld.xz.add(vec2(time.mul(0.025), time.mul(-0.014)));
  const cells = mx_worley_noise_vec2(wp.mul(hot ? 1.3 : 0.9));
  const gap = cells.y.sub(cells.x);
  const seam = float(1).sub(smoothstep(hot ? 0.08 : 0.025, hot ? 0.3 : 0.12, gap));
  const core = float(1).sub(smoothstep(0.008, hot ? 0.12 : 0.038, gap));
  const grain = mx_noise_float(vec3(wp.mul(2.5), 2)).mul(0.5).add(0.5);
  const crust = hot ? mix(vec3(0.12, 0.03, 0.012), vec3(0.32, 0.08, 0.02), grain) : mix(vec3(0.018, 0.022, 0.029), vec3(0.085, 0.055, 0.04), grain);
  const molten = mix(vec3(1.8, 0.16, 0.002), vec3(3.0, 0.95, 0.015), core);
  const edge = max(uv().x.sub(0.5).abs(), uv().y.sub(0.5).abs());
  const rim = smoothstep(0.46, 0.5, edge);
  m.colorNode = mix(mix(crust, molten, seam), vec3(0.06, 0.018, 0.008), rim);
  return m;
}
function plane(x: number, z: number, w: number, d: number, y: number): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(w, d); g.rotateX(-Math.PI / 2); g.translate(x, y, z); return g;
}
export function buildEmberTerrain(map: MapDef, grid: MapGrid): THREE.Group {
  const root = new THREE.Group(); root.name = 'ember-terrain';
  const basalt = basaltMaterial(), magma = magmaMaterial(), chasm = basaltMaterial(true);
  const add = (parts: THREE.BufferGeometry[], material: THREE.Material, name: string): void => {
    if (!parts.length) return;
    const mesh = new THREE.Mesh(merge(parts), material); mesh.name = name;
    mesh.receiveShadow = material !== magma; root.add(mesh);
  };
  const floors: THREE.BufferGeometry[] = [], cliffs: THREE.BufferGeometry[] = [], lava: THREE.BufferGeometry[] = [], fixtures: THREE.BufferGeometry[] = [];
  for (const f of map.floors) floors.push(plane((f.x0 + f.x1) / 2, (f.z0 + f.z1) / 2, f.x1 - f.x0, f.z1 - f.z0, 0));
  for (const f of map.lava ?? []) lava.push(plane((f.x0 + f.x1) / 2, (f.z0 + f.z1) / 2, f.x1 - f.x0, f.z1 - f.z0, 0.012));
  for (const r of map.chasms ?? []) {
    // 직사각형 전체가 빈칸이면 한 장으로 그려 용암에 1m 격자 테가 생기지 않게 한다.
    let empty = true;
    for (let z = r.z0; z < r.z1; z++) for (let x = r.x0; x < r.x1; x++) if (cellAt(grid, x + 0.5, z + 0.5) !== Cell.Void) empty = false;
    if (empty) { lava.push(plane((r.x0 + r.x1) / 2, (r.z0 + r.z1) / 2, r.x1 - r.x0, r.z1 - r.z0, -CHASM_DEPTH)); continue; }
    // 바닥과 겹친 셀은 빼서 다리 아래의 통행면을 덮지 않는다.
    for (let z = r.z0; z < r.z1; z++) for (let x = r.x0; x < r.x1; x++) {
      if (cellAt(grid, x + 0.5, z + 0.5) === Cell.Void) lava.push(plane(x + 0.5, z + 0.5, 1, 1, -CHASM_DEPTH));
    }
  }
  for (const e of emberEdges(map, grid)) {
    if (!e.chasm) continue;
    const w = e.nx ? 0.25 : 1.02, d = e.nz ? 0.25 : 1.02;
    const wall = new THREE.BoxGeometry(w, CHASM_DEPTH + BASALT_LIP, d);
    wall.translate(e.x - e.nx * 0.125, (BASALT_LIP - CHASM_DEPTH) / 2, e.z - e.nz * 0.125); cliffs.push(wall);
    // 양옆이 협곡인 3m 다리에는 기둥과 가는 수평 난간을 더한다(걸을 수 있는 폭은 유지).
    if (e.nx && chasmAt(map, grid, e.x + e.nx * 3.5, e.z)) {
      const post = new THREE.CylinderGeometry(0.13, 0.19, 1.1, 6); post.translate(e.x - e.nx * 0.12, 0.55, e.z); fixtures.push(post);
      const rail = new THREE.BoxGeometry(0.08, 0.08, 1.05); rail.translate(e.x - e.nx * 0.12, 0.87, e.z); fixtures.push(rail);
    }
  }
  for (const v of map.vents ?? []) {
    const rim = new THREE.TorusGeometry(0.58, 0.19, 5, 10); rim.rotateX(Math.PI / 2); rim.translate(v.x, 0.12, v.z); fixtures.push(rim);
    lava.push(plane(v.x, v.z, 0.9, 0.9, 0.018));
    for (let j = 0; j < 5; j++) {
      const a = j * Math.PI * 0.4, stone = new THREE.SphereGeometry(0.23, 5, 3);
      stone.translate(v.x + Math.cos(a) * 0.75, 0.14, v.z + Math.sin(a) * 0.75); fixtures.push(stone);
    }
  }
  add(floors, emberFloorMaterial(map, null), 'basalt-floor'); add(cliffs, chasm, 'chasm-faces-8m'); add(fixtures, basalt, 'bridge-rails-vent-rims'); add(lava, magma, 'magma');

  // 검은 구름과 주황 지평선: R1 숲 파노라마를 읽지 않는다.
  root.add(emberSky());
  root.add(emberAtmosphere(map, false), emberAtmosphere(map, true));
  return root;
}
/**
 * 잿불 하늘(화면 전체 판, 깊이 없음): 검은 연기 구름과 주황 지평선. 지역2 스테이지 지도(S6)·레이드 둥지도 같은 하늘을 쓴다.
 * low/high = 아래(지평선)·위 색(지도·시간대마다 바꾼다).
 * C8(2026-10-30): 두 색 단색 판이 레이드 위쪽에서 '단색 보라'로 읽혀(L11) 세 단 그라데이션 + 두 겹 흐르는 연기(아래쪽은 용암빛을 받아 밝다)
 * + 지평선에서 올라가는 잔불 점을 넣었다. 노이즈 3번(연기 2·잔불 1)만 쓰고 화면 위쪽 띠에서만 보인다(나머지는 바닥이 덮는다).
 */
export function emberSky(low: readonly [number, number, number] = [0.75, 0.13, 0.025], high: readonly [number, number, number] = [0.018, 0.009, 0.014], detail = true): THREE.Mesh {
  const skyMat = new THREE.MeshBasicNodeMaterial({ depthWrite: false, depthTest: false, fog: false });
  skyMat.vertexNode = vec4(positionGeometry.xy, 0.5, 1);
  const lo = vec3(low[0], low[1], low[2]), hi = vec3(high[0], high[1], high[2]);
  const y = screenUV.y; // 0 = 화면 위, 1 = 아래(지평선 쪽)
  // 세 단: 위는 거의 검은 연기, 가운데는 지평선 색을 어둡게 탁한 띠, 아래로 갈수록 용암빛
  const mid = mix(hi, lo, 0.32).mul(vec3(0.85, 0.7, 0.75));
  const grad = mix(mix(hi, mid, smoothstep(0.0, 0.45, y)), lo, smoothstep(0.38, 0.95, y));
  // 연기 두 겹: 가로로 늘인 노이즈가 서로 다른 속도로 흐른다. 큰 겹은 느린 노이즈로 휘게(도메인 왜곡) 해 뭉게구름 덩어리로 보인다
  const t = time.mul(0.012);
  // 저사양(detail=false)은 왜곡·아랫면 테 노이즈 2번을 빼고 C8 전과 같은 3번만 쓴다
  const warp = detail ? mx_noise_float(vec3(screenUV.x.mul(1.3).sub(t.mul(0.5)), y.mul(4), 7.3)).mul(0.7) : float(0);
  const c1 = mx_noise_float(vec3(screenUV.x.mul(2.2).add(t).add(warp), y.mul(9), 1.7)).mul(0.5).add(0.5);
  const c2 = mx_noise_float(vec3(screenUV.x.mul(5.3).sub(t.mul(1.8)).add(warp), y.mul(16), 4.1)).mul(0.5).add(0.5);
  const dens = smoothstep(0.4, 0.72, c1.mul(0.65).add(c2.mul(0.35)));
  // 구름 아랫면 테: 조금 아래(화면 y+0.035)의 큰 겹 밀도를 빼면 구름 덩어리의 아래 가장자리만 남는다 → 용암빛으로 밝힌다.
  // C8-2(회차3 비평 '하늘이 마젠타 단색, 연기 구름·광원 없음'): 어두운 판 위에 더 어두운 연기만 있어 구름이 안 보였다
  const c1b = detail ? mx_noise_float(vec3(screenUV.x.mul(2.2).add(t).add(warp), y.add(0.035).mul(9), 1.7)).mul(0.5).add(0.5) : null;
  const edge = c1b ? dens.sub(smoothstep(0.4, 0.72, c1b.mul(0.65).add(c2.mul(0.35)))).clamp(0, 1) : float(0);
  // 연기 몸은 검은 재색(위)~검붉은색(아래), 아랫면 테는 지평선 색으로 달아오른다
  const under = smoothstep(0.0, 0.6, y);
  const smoke = mix(vec3(0.03, 0.022, 0.024), lo.mul(0.32), under.mul(smoothstep(0.65, 0.35, c2)).add(0.15));
  let col = mix(grad, smoke, dens.mul(0.92));
  col = col.add(lo.mul(edge.mul(0.85)));
  // 지평선 아지랑이: 맨 아래 가는 밝은 띠
  col = col.add(lo.mul(smoothstep(0.86, 1.0, y).mul(0.2)));
  // 잔불: 위로 흘러가는 아주 작은 점(회차1 비평: 큰 점은 보케처럼 보였다)(아래 절반에서 잘 보이고 위로 갈수록 사라진다)
  const ep = vec2(screenUV.x.mul(90), y.mul(48).add(time.mul(1.4)));
  const ed = mx_worley_noise_vec2(ep).x;
  const spark = smoothstep(0.06, 0.0, ed).mul(smoothstep(0.15, 0.7, y)).mul(float(1).sub(dens.mul(0.6)));
  col = col.add(vec3(1.0, 0.45, 0.1).mul(spark.mul(0.9)));
  skyMat.colorNode = col;
  const sky = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), skyMat); sky.frustumCulled = false; sky.renderOrder = -1000; sky.name = 'ember-sky';
  return sky;
}
/**
 * 용암 웅덩이(판정 칸과 같은 사각형, 바닥 바로 위 발광 판) + 분출구 테(현무암 고리·돌). 지역2 스테이지 지도(S6)의 위험 표시.
 * 보이는 곳이 위험한 곳이다(R2 지역 지도와 같은 재질).
 */
export function emberHazards(map: MapDef): THREE.Group {
  const root = new THREE.Group(); root.name = 'ember-hazards';
  const lava: THREE.BufferGeometry[] = [], fixtures: THREE.BufferGeometry[] = [];
  for (const f of map.lava ?? []) lava.push(plane((f.x0 + f.x1) / 2, (f.z0 + f.z1) / 2, f.x1 - f.x0, f.z1 - f.z0, 0.03));
  for (const v of map.vents ?? []) {
    const rim = new THREE.TorusGeometry(0.58, 0.19, 5, 10); rim.rotateX(Math.PI / 2); rim.translate(v.x, 0.12, v.z); fixtures.push(rim);
    lava.push(plane(v.x, v.z, 0.9, 0.9, 0.04));
    for (let j = 0; j < 5; j++) {
      const a = j * Math.PI * 0.4, stone = new THREE.SphereGeometry(0.23, 5, 3);
      stone.translate(v.x + Math.cos(a) * 0.75, 0.14, v.z + Math.sin(a) * 0.75); fixtures.push(stone);
    }
  }
  if (lava.length) { const m = new THREE.Mesh(merge(lava), magmaMaterial(true)); m.name = 'magma'; root.add(m); }
  if (fixtures.length) { const m = new THREE.Mesh(merge(fixtures), basaltMaterial()); m.name = 'vent-rims'; m.receiveShadow = true; root.add(m); }
  return root;
}
/** 떠오르는 불씨(가산)·분출구 연기. extra = 더 띄울 자리(x, y, z 셋씩 — 지역2 스테이지 지도는 가장자리 아래 용암 면) */
export function emberAtmosphere(map: MapDef, smoke: boolean, extra: readonly number[] = []): THREE.InstancedMesh {
  const rnd = mulberry32(smoke ? 71 : 91);
  const origins: number[] = [];
  for (const v of map.vents ?? []) for (let i = 0; i < (smoke ? 3 : 8); i++) origins.push(v.x, 0.2, v.z, rnd());
  if (!smoke) for (const r of map.chasms ?? []) for (let i = 0; i < 18; i++) origins.push(r.x0 + rnd() * (r.x1 - r.x0), -CHASM_DEPTH, r.z0 + rnd() * (r.z1 - r.z0), rnd());
  for (let i = 0; i + 2 < extra.length; i += 3) origins.push(extra[i], extra[i + 1], extra[i + 2], rnd());
  const g = new THREE.PlaneGeometry(smoke ? 1.3 : 0.07, smoke ? 1.8 : 0.15);
  g.setAttribute('aEmber', new THREE.InstancedBufferAttribute(new Float32Array(origins), 4));
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: smoke ? THREE.NormalBlending : THREE.AdditiveBlending });
  const a = attribute('aEmber', 'vec4');
  const age = fract(time.mul(smoke ? 0.14 : 0.12).add(a.w));
  const drift = sin(age.mul(5).add(a.w.mul(9))).mul(smoke ? 0.4 : 0.7);
  m.positionNode = positionGeometry.add(vec3(a.x.add(drift), a.y.add(age.mul(smoke ? 3 : 10)), a.z));
  m.colorNode = smoke ? vec3(0.07, 0.045, 0.05) : vec3(2.5, 0.35, 0.015);
  m.opacityNode = float(1).sub(smoothstep(0.2, 0.5, length(uv().sub(0.5)))).mul(sin(age.mul(Math.PI))).mul(smoke ? 0.26 : 0.75);
  const mesh = new THREE.InstancedMesh(g, m, origins.length / 4); mesh.frustumCulled = false; mesh.name = smoke ? 'vent-smoke' : 'rising-embers';
  return mesh;
}
